import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Empty,
  Input,
  Popconfirm,
  Spin,
  Tooltip,
  message,
} from "antd";
import {
  CheckOutlined,
  CloseOutlined,
  LinkOutlined,
} from "@ant-design/icons";
import {
  confirmEmailLink,
  getPendingEmailLinks,
  getUnmatchedEmails,
  linkEmailToRecord,
  rejectEmailLink,
  type EmailLinkType,
  type EmailMessageRecord,
  type EmailProposal,
  type PendingEmailRecord,
} from "../../utils/emailLinkAPI";
import {
  MessageCard,
  MessageListRow,
  OpenOutlookButton,
  POPCONFIRM_DANGER_OK_PROPS,
  PRIMARY_BUTTON_CLASS,
} from "./emailLinkShared";

// The two safety-net lists behind each Email tab. Both are generic over
// linkType, so Part 2 reuses them exactly as Part 1 does.
//
//   UnmatchedEmailList  - mail carrying no confirmed link. Nothing is lost:
//                         every unmatched message stays visible here until
//                         someone attaches it. This is where the one-click
//                         manual attach lives (the buying-agent email with no
//                         quote number, the dealer reply that matches nothing).
//
//   PendingLinkList     - non-exact matches the matcher REFUSED to link on its
//                         own. Sean's rule: confirm, never silent. These are
//                         proposals until a human clicks Confirm.

const PAGE_SIZE = 20;

// ─── Unmatched ───────────────────────────────────────────────────────────────

export interface UnmatchedEmailListProps {
  linkType: EmailLinkType;
  keyLabel: string;
  /** Called after a successful attach so the parent can refresh its history. */
  onAttached?: () => void;
  refreshToken?: number;
  /**
   * Bumped by the page while a sync is bringing in mail. Unlike refreshToken it
   * refreshes quietly: no spinner, no jump back to the top, and the rows already
   * on screen (and any extra pages loaded) stay put - new mail is just merged in.
   */
  liveToken?: number;
  /**
   * Current filter. The search box itself lives in the page's tab bar, next to
   * Refresh, so this list carries no toolbar of its own.
   */
  search?: string;
  /** "out" narrows the list to Sent Mail, set by the folder sidebar. */
  direction?: "in" | "out";
  /**
   * Hands the selected email's actions — Open in Email, Open Outlook, and the
   * attach control — to the page shell, which renders them in the tab bar so
   * the reading pane keeps its full height.
   */
  onActions?: (actions: React.ReactNode) => void;
  /** Reports the total row count up, so the folder sidebar can badge Inbox. */
  onCount?: (total: number) => void;
}

export const UnmatchedEmailList: React.FC<UnmatchedEmailListProps> = ({
  linkType,
  keyLabel,
  onAttached,
  refreshToken = 0,
  liveToken = 0,
  search = "",
  direction,
  onActions,
  onCount,
}) => {
  const [records, setRecords] = useState<EmailMessageRecord[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  // Kept apart from `loading` so fetching the next page never blanks the list
  // behind a full-pane spinner while you are reading a message.
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  // Per-row attach target, so several rows can be prepared independently.
  const [attachKeys, setAttachKeys] = useState<Record<number, string>>({});
  const [attachingId, setAttachingId] = useState<number | null>(null);

  const selected = records.find((item) => item.id === selectedId) || null;
  const hasMore = records.length < total;

  const fetchPage = useCallback(
    async (targetPage: number, append: boolean) => {
      if (append) setLoadingMore(true);
      else setLoading(true);
      setLoadError(null);
      try {
        const response = await getUnmatchedEmails({
          linkType,
          search: search || undefined,
          direction,
          page: targetPage,
          limit: PAGE_SIZE,
        });
        setRecords((current) =>
          append
            ? [
                ...current,
                // A sync landing between two page fetches can shift the window
                // and repeat a row; drop anything already on screen.
                ...response.data.filter(
                  (item) => !current.some((existing) => existing.id === item.id),
                ),
              ]
            : response.data,
        );
        setTotal(response.total);
        setPage(targetPage);
      } catch (error: any) {
        setLoadError(
          error?.response?.data?.message ||
            error?.message ||
            "Could not load unmatched emails",
        );
      } finally {
        setLoadingMore(false);
        setLoading(false);
      }
    },
    [linkType, search, direction],
  );

  // Back to the top whenever the filter, the side, or the refresh token moves.
  useEffect(() => {
    fetchPage(1, false);
  }, [fetchPage, refreshToken]);

  // Quiet refresh while mail is still arriving: fetch the newest page and merge
  // anything not on screen yet, newest first.
  useEffect(() => {
    if (!liveToken) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const response = await getUnmatchedEmails({
          linkType,
          search: search || undefined,
          direction,
          page: 1,
          limit: PAGE_SIZE,
        });
        if (cancelled) return;
        setRecords((current) => {
          const known = new Set(current.map((item) => item.id));
          const fresh = response.data.filter((item) => !known.has(item.id));
          if (!fresh.length) return current;
          const when = (item: EmailMessageRecord) =>
            new Date(item.sentAt || item.receivedAt || 0).getTime();
          return [...fresh, ...current].sort((a, b) => when(b) - when(a) || b.id - a.id);
        });
        setTotal(response.total);
      } catch {
        // A missed quiet refresh is harmless - the next tick tries again.
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [liveToken]);

  // Infinite scroll: the next page is pulled as the list nears its end, so the
  // pane fills itself instead of asking for a page number.
  const handleListScroll = (event: React.UIEvent<HTMLDivElement>) => {
    if (loading || loadingMore || !hasMore) return;
    const el = event.currentTarget;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 160) {
      fetchPage(page + 1, true);
    }
  };

  // Keep a message selected for the reading pane — default to the first.
  useEffect(() => {
    setSelectedId((current) => {
      if (current && records.some((item) => item.id === current)) return current;
      return records.length ? records[0].id : null;
    });
  }, [records]);

  const attach = async (record: EmailMessageRecord) => {
    const value = (attachKeys[record.id] ?? "").trim();
    if (!value) {
      message.warning(`Enter the ${keyLabel.toLowerCase()} to attach this email to`);
      return;
    }

    setAttachingId(record.id);
    try {
      await linkEmailToRecord(
        record.id,
        linkType === "quote"
          ? { linkType: "quote", quoteId: value }
          : { linkType: "dealer", model: value },
      );
      message.success(`Email attached to ${keyLabel.toLowerCase()} ${value}`);
      setAttachKeys((current) => {
        const next = { ...current };
        delete next[record.id];
        return next;
      });
      onAttached?.();
      await fetchPage(1, false);
    } catch (error: any) {
      message.error(
        error?.response?.data?.message || error?.message || "Could not attach the email",
      );
    } finally {
      setAttachingId(null);
    }
  };

  // The selected email's actions. Rendered by the page shell in the tab bar,
  // not inside the card, so the message body keeps the height.
  const actions = selected ? (
    <div className="flex flex-wrap items-start gap-2">
      <OpenOutlookButton record={selected} size="middle" />

      {/* Styled like the search box above it — a prefixed input, no label —
          and every control in the row is size="middle" so the three line up. */}
      <div className="flex flex-col gap-1">
        <Input
          size="middle"
          placeholder={`Attaches this email to a ${keyLabel.toLowerCase()}`}
          prefix={<LinkOutlined className="text-gray-400" />}
          className="!w-64"
          value={attachKeys[selected.id] ?? ""}
          onChange={(event) =>
            setAttachKeys((current) => ({
              ...current,
              [selected.id]: event.target.value,
            }))
          }
          onPressEnter={() => attach(selected)}
          allowClear
        />
      </div>
      <Tooltip title={`Attach this email to a ${keyLabel.toLowerCase()}`}>
        <Button
          size="middle"
          type="primary"
          className={PRIMARY_BUTTON_CLASS}
          loading={attachingId === selected.id}
          onClick={() => attach(selected)}
        >
          Attach
        </Button>
      </Tooltip>
    </div>
  ) : null;

  useEffect(() => {
    onActions?.(actions);
    // `actions` is rebuilt on every render; the effect must follow the state it
    // reads, not the node identity, or it would loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onActions, selected, attachKeys, attachingId, keyLabel]);

  useEffect(() => {
    onCount?.(total);
  }, [onCount, total]);

  return (
    <div className="flex h-full min-h-0 flex-col gap-3">
      {loadError ? <Alert type="error" showIcon message={loadError} /> : null}

      <Spin
        spinning={loading}
        wrapperClassName={
          "flex min-h-0 flex-1 flex-col " +
          "[&_.ant-spin-container]:flex [&_.ant-spin-container]:min-h-0 " +
          "[&_.ant-spin-container]:flex-1 [&_.ant-spin-container]:flex-col"
        }
      >
        {!records.length && !loading ? (
          <Empty
            description={
              direction === "out" ? "No unmatched sent emails" : "No unmatched emails"
            }
            className="!py-16"
          />
        ) : (
          <div className="grid min-h-0 flex-1 grid-cols-1 gap-4 lg:grid-cols-[320px_1fr] xl:grid-cols-[360px_1fr] 2xl:grid-cols-[420px_1fr]">
            {/* Left: the full unmatched list, Outlook-style. */}
            <div
              onScroll={handleListScroll}
              className="max-h-[55vh] divide-y divide-gray-100 overflow-auto rounded-lg border border-gray-200 bg-white lg:max-h-none lg:min-h-0"
            >
              {records.map((record) => (
                <MessageListRow
                  key={record.id}
                  record={record}
                  active={record.id === selectedId}
                  onClick={() => setSelectedId(record.id)}
                />
              ))}

              {loadingMore ? (
                <div className="flex items-center justify-center gap-2 py-3 text-xs text-gray-400">
                  <Spin size="small" /> Loading more…
                </div>
              ) : !hasMore && records.length ? (
                <div className="py-3 text-center text-xs text-gray-300">
                  {records.length} of {total} — end of the list
                </div>
              ) : null}
            </div>

            {/* Right: the selected email, with the attach control in the tab
                bar above and Reply/Reply all in the card's own header. */}
            <div className="min-h-0 overflow-auto lg:overflow-hidden lg:pr-1">
              {selected ? (
                <MessageCard
                  record={selected}
                  showCommandBar={false}
                  onReplySent={() => fetchPage(1, false)}
                  fill
                />
              ) : (
                <Empty description="Select an email" className="!py-16" />
              )}
            </div>
          </div>
        )}
      </Spin>

    </div>
  );
};

// ─── Needs Confirm ────────────────────────────────────────────────────────────────

export interface PendingLinkListProps {
  linkType: EmailLinkType;
  keyLabel: string;
  onResolved?: () => void;
  refreshToken?: number;
}

export const PendingLinkList: React.FC<PendingLinkListProps> = ({
  linkType,
  keyLabel,
  onResolved,
  refreshToken = 0,
}) => {
  const [records, setRecords] = useState<PendingEmailRecord[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [busyLinkId, setBusyLinkId] = useState<number | null>(null);

  // The API returns one row per PROPOSAL, so a newsletter the matcher liked
  // against six models arrives as six rows of the same email. Group them back
  // into one row per email, each carrying its own list of proposals.
  const groups = useMemo(() => {
    const byEmail = new Map<
      number,
      { record: PendingEmailRecord; proposals: EmailProposal[] }
    >();
    for (const item of records) {
      const group = byEmail.get(item.id);
      if (!group) {
        byEmail.set(item.id, { record: item, proposals: [item.proposal] });
      } else if (
        !group.proposals.some((p) => p.linkId === item.proposal.linkId)
      ) {
        group.proposals.push(item.proposal);
      }
    }
    return Array.from(byEmail.values());
  }, [records]);

  const selected = groups.find((item) => item.record.id === selectedId) || null;
  const hasMore = records.length < total;

  const fetchPage = useCallback(
    async (targetPage: number, append: boolean) => {
      if (append) setLoadingMore(true);
      else setLoading(true);
      setLoadError(null);
      try {
        const response = await getPendingEmailLinks({
          linkType,
          page: targetPage,
          limit: PAGE_SIZE,
        });
        setRecords((current) =>
          append
            ? [
                ...current,
                ...response.data.filter(
                  (item) => !current.some((existing) => existing.id === item.id),
                ),
              ]
            : response.data,
        );
        setTotal(response.total);
        setPage(targetPage);
      } catch (error: any) {
        setLoadError(
          error?.response?.data?.message ||
            error?.message ||
            "Could not load pending matches",
        );
      } finally {
        setLoadingMore(false);
        setLoading(false);
      }
    },
    [linkType],
  );

  useEffect(() => {
    fetchPage(1, false);
  }, [fetchPage, refreshToken]);

  // Keep an email selected for the reading pane — default to the first.
  useEffect(() => {
    setSelectedId((current) => {
      if (current && groups.some((item) => item.record.id === current)) {
        return current;
      }
      return groups.length ? groups[0].record.id : null;
    });
  }, [groups]);

  const handleListScroll = (event: React.UIEvent<HTMLDivElement>) => {
    if (loading || loadingMore || !hasMore) return;
    const el = event.currentTarget;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 160) {
      fetchPage(page + 1, true);
    }
  };

  const resolve = async (linkId: number, action: "confirm" | "reject") => {
    setBusyLinkId(linkId);
    try {
      if (action === "confirm") {
        await confirmEmailLink(linkId);
        message.success("Link confirmed");
      } else {
        await rejectEmailLink(linkId);
        message.success("Match rejected — it will not be proposed again");
      }
      onResolved?.();
      await fetchPage(1, false);
    } catch (error: any) {
      message.error(
        error?.response?.data?.message || error?.message || "Could not update the match",
      );
    } finally {
      setBusyLinkId(null);
    }
  };

  const proposedKeyOf = (proposal: EmailProposal) =>
    proposal.quoteId || proposal.model || "-";

  // Every proposal on the open email, each with what it matched, how strongly,
  // and its own Confirm / Reject — one email can be proposed against several
  // records and each decision is separate.
  const proposalBar = selected ? (
    <div className="shrink-0 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2">
      <div className="mb-1.5 text-[11px] font-medium text-amber-800">
        {selected.proposals.length} proposed match
        {selected.proposals.length === 1 ? "" : "es"} — nothing is linked until
        you confirm
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {selected.proposals.map((proposal) => (
          <div
            key={proposal.linkId}
            className="flex items-center gap-1.5 rounded-md border border-amber-200 bg-white py-1 pl-1.5 pr-1"
          >
            {/* Shown so whoever confirms can see WHY it was proposed, rather
                than trusting an opaque score. */}
            <Tooltip
              title={`Matched on "${proposal.matchedText}" found in the ${proposal.matchField}`}
            >
              <span className="text-xs font-medium text-gray-700">
                {keyLabel} {proposedKeyOf(proposal)}
                {proposal.confidence != null ? (
                  <span className="ml-1 text-amber-700">
                    {Math.round(proposal.confidence * 100)}%
                  </span>
                ) : null}
              </span>
            </Tooltip>
            <Button
              size="small"
              type="primary"
              className={PRIMARY_BUTTON_CLASS}
              icon={<CheckOutlined />}
              loading={busyLinkId === proposal.linkId}
              onClick={() => resolve(proposal.linkId, "confirm")}
            >
              Confirm
            </Button>
            <Popconfirm
              title="Reject this match?"
              description="It will not be proposed again on future syncs."
              okText="Reject"
              okButtonProps={POPCONFIRM_DANGER_OK_PROPS}
              onConfirm={() => resolve(proposal.linkId, "reject")}
            >
              <Button
                size="small"
                danger
                icon={<CloseOutlined />}
                loading={busyLinkId === proposal.linkId}
              >
                Reject
              </Button>
            </Popconfirm>
          </div>
        ))}
      </div>
    </div>
  ) : null;

  return (
    <div className="flex h-full min-h-0 flex-col gap-3">
      {loadError ? <Alert type="error" showIcon message={loadError} /> : null}

      <Spin
        spinning={loading}
        wrapperClassName={
          "flex min-h-0 flex-1 flex-col " +
          "[&_.ant-spin-container]:flex [&_.ant-spin-container]:min-h-0 " +
          "[&_.ant-spin-container]:flex-1 [&_.ant-spin-container]:flex-col"
        }
      >
        {!records.length && !loading ? (
          <Empty description="Nothing waiting for confirmation" className="!py-16" />
        ) : (
          <div className="grid min-h-0 flex-1 grid-cols-1 gap-4 lg:grid-cols-[320px_1fr] xl:grid-cols-[360px_1fr] 2xl:grid-cols-[420px_1fr]">
            {/* Left: every proposal, with what it was matched to under the
                preview so the list can be triaged without opening each one. */}
            <div
              onScroll={handleListScroll}
              className="max-h-[55vh] divide-y divide-gray-100 overflow-auto rounded-lg border border-gray-200 bg-white lg:max-h-none lg:min-h-0"
            >
              {groups.map(({ record, proposals }) => (
                <MessageListRow
                  key={record.id}
                  record={record}
                  active={record.id === selectedId}
                  onClick={() => setSelectedId(record.id)}
                  note={
                    proposals.length === 1
                      ? `Proposed: ${keyLabel} ${proposedKeyOf(proposals[0])}${
                          proposals[0].confidence != null
                            ? ` · ${Math.round(proposals[0].confidence * 100)}%`
                            : ""
                        }`
                      : `${proposals.length} proposed matches`
                  }
                />
              ))}

              {loadingMore ? (
                <div className="flex items-center justify-center gap-2 py-3 text-xs text-gray-400">
                  <Spin size="small" /> Loading more…
                </div>
              ) : !hasMore && records.length ? (
                <div className="py-3 text-center text-xs text-gray-300">
                  {groups.length} email{groups.length === 1 ? "" : "s"} · {total}{" "}
                  proposed match{total === 1 ? "" : "es"}
                </div>
              ) : null}
            </div>

            {/* Right: the proposals, then the email itself. No reply box —
                nothing here is linked yet, so there is no conversation to reply
                into. */}
            <div className="flex min-h-0 flex-col gap-2 overflow-auto lg:overflow-hidden lg:pr-1">
              {selected ? (
                <>
                  {proposalBar}
                  <div className="min-h-0 flex-1">
                    <MessageCard
                      record={selected.record}
                      showReply={false}
                      showCommandBar={false}
                      fill
                    />
                  </div>
                </>
              ) : (
                <Empty description="Select an email" className="!py-16" />
              )}
            </div>
          </div>
        )}
      </Spin>
    </div>
  );
};
